/* import __COLOCATED_TEMPLATE__ from './side-panel.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type AppService from 'embercom/services/app-service';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import type Model from '@ember-data/model';
import { get } from 'embercom/lib/ajax';
import { DeduplicatedAsyncData } from 'embercom/resources/utils/async-data';
import { type LocalizedKnowledgeContent } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import { type EntityScoreData } from 'embercom/components/content-readiness/types';

export interface AIScorePillData {
  totalScore: number;
  recommendationCount: number;
}

interface Signature {
  Args: {
    activeContent: LocalizedKnowledgeContent & Model;
    isOpen: boolean;
    onClose: () => void;
    onPillDataChange?: (data: AIScorePillData | null) => void;
    onIssueClick?: (blockIndex: number) => void;
  };
}

export default class ContentReadinessSidePanel extends Component<Signature> {
  @service declare appService: AppService;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;

  @tracked private pillRecommendationCount = 0;

  private scoreLoader = DeduplicatedAsyncData(
    this,
    () => [
      this.args.activeContent.entityId,
      this.args.activeContent.entityType,
      (this.args.activeContent.latestVersion as (Model & { id: string }) | undefined)?.id,
    ],
    async (
      entityId: number,
      entityType: number,
      entityVersionId: string | undefined,
    ): Promise<{ score: EntityScoreData } | null> => {
      if (!entityId || !entityType || !entityVersionId) {
        this.args.onPillDataChange?.(null);
        return null;
      }

      let result = (await get('/teammate_app/content_readiness/entity_score', {
        app_id: this.appService.app.id,
        entity_type: entityType,
        entity_id: entityId,
        entity_version_id: entityVersionId,
      })) as { score: EntityScoreData };

      if (result?.score) {
        this.pillRecommendationCount = result.score.rule_results
          ? Object.values(result.score.rule_results).reduce(
              (sum, rule) => sum + rule.issue_count,
              0,
            )
          : 0;
        this.args.onPillDataChange?.({
          totalScore: result.score.total_score,
          recommendationCount: this.pillRecommendationCount,
        });
      }

      return result;
    },
  );

  get scoreData(): EntityScoreData | null {
    return this.scoreLoader.value?.score ?? null;
  }

  get isLoading(): boolean {
    return this.scoreLoader.isLoading;
  }

  get error(): unknown {
    return this.scoreLoader.error;
  }

  @action
  handleIssueReviewed() {
    this.pillRecommendationCount = Math.max(0, this.pillRecommendationCount - 1);

    let currentScore = this.scoreData;
    if (currentScore) {
      this.args.onPillDataChange?.({
        totalScore: currentScore.total_score,
        recommendationCount: this.pillRecommendationCount,
      });
    }

    this.reactTeammateAppIsland.dispatchTeammateAppEvent('content-readiness:issue-reviewed');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'ContentReadiness::SidePanel': typeof ContentReadinessSidePanel;
    'content-readiness/side-panel': typeof ContentReadinessSidePanel;
  }
}
